import React from 'react';
import PropTypes from 'prop-types';
import { FiEye } from 'react-icons/fi';
import { GrStatusGood } from 'react-icons/gr';

const listWrapperStyle = {
    width: '100%',
    margin: '10px auto',
};

const defaultLabelStyle = {
    marginTop: '10px',
    marginBottom: '6px',
    fontWeight: 600,
    textAlign: 'center',
    letterSpacing: '.02em',
};

const baseListStyle = {
    width: '100%',
    maxHeight: 200,
    overflowY: 'scroll',
    textAlign: 'left',
    padding: '18px 10px',
    background: 'linear-gradient(145deg, rgba(192, 209, 212, 0.55), rgba(62, 143, 147, 0.08))',
    borderRadius: '14px',
    wordBreak: 'break-word',
    lineHeight: 1,
    color: '#0c1229',
    border: '1px solid rgba(62, 143, 147, 0.25)',
    boxShadow: '0 20px 45px rgba(4, 28, 40, 0.12) inset, 0 10px 30px rgba(4, 28, 40, 0.08)',
};

const baseRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    width: '100%',
    borderRadius: '12px',
    padding: '14px 16px',
    fontSize: '.76rem',
    fontWeight: 500,
    marginBottom: '10px',
    cursor: 'pointer',
    background: 'rgba(255, 255, 255, 0.75)',
    border: '1px dashed rgba(12, 18, 41, 0.3)',
    transition: 'transform 0.25s ease, border 0.25s ease, background 0.25s ease',
};

const activeRowStyle = {
    color: '#0d3f42',
    border: '1px solid #3E8F93',
    background: 'linear-gradient(120deg, rgba(62, 143, 147, 0.18), rgba(255, 255, 255, 0.9))',
    boxShadow: '0 6px 20px rgba(62, 143, 147, 0.25)',
};

const previewButtonStyle = {
    width: 42,
    height: 42,
    borderRadius: '14px',
    border: '1px solid rgba(62, 143, 147, 0.65)',
    background: 'radial-gradient(circle at top, rgba(255,255,255,.9), rgba(62,143,147,.2))',
    color: '#0d3f42',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: '0 4px 12px rgba(62, 143, 147, 0.25)',
};

const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '999px',
    background: '#0d3f42',
    color: '#7ef0ff',
    width: 26,
    height: 26,
    boxShadow: '0 0 8px rgba(62, 143, 147, 0.45)',
};

const emptyStateStyle = {
    fontSize: '.82rem',
    opacity: 0.8,
    textAlign: 'center',
    padding: '12px 0',
};

const ResumeSelectList = ({
    label = '',
    labelStyle = {},
    resumes = [],
    selectedIndex = 0,
    onSelectResume,
    onPreviewResume,
    maxHeight = 200,
    emptyMessage = 'No resumes yet. Upload or create one first.',
    containerStyle = {},
    dataTest,
}) => {
    const mergedListStyle = { ...baseListStyle, maxHeight, ...containerStyle };

    const renderResumeRow = (resume, idx) => {
        const isActive = selectedIndex === idx + 1;
        const key = resume?.id || resume?._id || resume?.storageDetails?.name || `resume-${idx}`;

        const handleRowClick = () => {
            if (typeof onSelectResume === 'function') {
                onSelectResume(idx, resume);
            }
        };

        const handleKeyDown = (event) => {
            if (!onSelectResume) return;
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelectResume(idx, resume);
            }
        };

        const handlePreview = (event) => {
            event.stopPropagation();
            if (typeof onPreviewResume === 'function') {
                onPreviewResume(resume, idx);
            }
        };

        const rowStyle = isActive ? { ...baseRowStyle, ...activeRowStyle } : baseRowStyle;

        return (
            <div
                key={key}
                style={rowStyle}
                role="button"
                tabIndex={0}
                aria-pressed={isActive}
                data-test={dataTest ? `${dataTest}-row-${idx}` : undefined}
                onClick={handleRowClick}
                onKeyDown={handleKeyDown}
            >
                <button
                    type="button"
                    onClick={handlePreview}
                    style={previewButtonStyle}
                    aria-label="Preview resume"
                >
                    <FiEye size={18} />
                </button>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1, gap: 12 }}>
                    <span style={{ fontWeight: 600, letterSpacing: '.01em' }}>
                        {resume?.storageDetails?.name || `Resume ${idx + 1}`}
                    </span>
                    {isActive && (
                        <span style={badgeStyle}>
                            <GrStatusGood size={16} />
                        </span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div style={listWrapperStyle}>
            {label && (
                <div style={{ ...defaultLabelStyle, ...labelStyle }}>
                    {label}
                </div>
            )}
            <div style={mergedListStyle} data-test={dataTest}>
                {Array.isArray(resumes) && resumes.length > 0 ? (
                    resumes.map(renderResumeRow)
                ) : (
                    <div style={emptyStateStyle}>{emptyMessage}</div>
                )}
            </div>
        </div>
    );
};

ResumeSelectList.propTypes = {
    label: PropTypes.string,
    labelStyle: PropTypes.object,
    resumes: PropTypes.arrayOf(PropTypes.object),
    selectedIndex: PropTypes.number,
    onSelectResume: PropTypes.func,
    onPreviewResume: PropTypes.func,
    maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    emptyMessage: PropTypes.string,
    containerStyle: PropTypes.object,
    dataTest: PropTypes.string,
};

export default ResumeSelectList;
